import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { authAPI } from '../services/api';
import { User, Mail, Building2, Phone, Save, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const Profile = () => {
  const { user, updateUser, isDeveloper } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await authAPI.updateProfile(formData);
      const userData = response?.data?.data || response?.data;
      updateUser(userData);
      toast.success('Perfil actualizado');
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      toast.error('Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="profile-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Mi Perfil</h1>
          <p className="text-[#5C554B]">Gestiona tu información personal</p>
        </div>

        {/* Profile Card */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 bg-[#C05621] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#2D2A26]">{user?.name}</h2>
                <p className="text-[#5C554B]">{user?.email}</p>
                <Badge className={`mt-2 ${isDeveloper() ? 'bg-[#C05621]/10 text-[#C05621]' : 'bg-[#81A183]/10 text-[#81A183]'}`}>
                  {user?.role === 'desarrollador' ? 'Desarrollador' : 'Constructor'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <form onSubmit={handleSubmit}>
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle>Información Personal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#5C554B]">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="email"
                    value={user?.email}
                    className="input-default pl-12 bg-[#FDFBF7]"
                    disabled
                  />
                </div>
                <p className="text-xs text-[#9CA3AF]">El correo no se puede modificar</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nombre completo</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-default pl-12"
                    placeholder="Tu nombre"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Empresa</Label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input-default pl-12"
                    placeholder="Nombre de tu empresa"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-default pl-12"
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Guardar cambios
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Account Info */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle>Información de Cuenta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-[#E2E8F0]">
                <span className="text-[#5C554B]">Tipo de cuenta</span>
                <span className="font-medium text-[#2D2A26] capitalize">{user?.role}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-[#E2E8F0]">
                <span className="text-[#5C554B]">Estado</span>
                <Badge className="bg-green-100 text-green-800">Activo</Badge>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-[#5C554B]">ID de usuario</span>
                <span className="font-mono text-sm text-[#9CA3AF]">{user?.id?.slice(0, 8)}...</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
